import { Add } from "@carbon/icons-react";
import {
  Button,
  Layer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from "@carbon/react";
import { SingleSelectAppliedFilter, useModal } from "@flaminem/ui-components";
import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { AppliedFilters } from "src/components/Filters/AppliedFilters.tsx";
import { ModalTransition } from "src/components/Modal";
import { debounceSearchTime } from "src/hooks/useSearch.ts";
import { useT } from "src/internationalization";
import { debounce } from "src/utils/functions.utils.ts";

import { BlacklistElementModal } from "./BlacklistElementModal";
import { BlacklistsFilter } from "./BlacklistsFilter";

export type BlacklistsTableToolbarProps = {
  onElementCreated: () => void;
  searchTerm: string;
  handleOnSearchTermChange: (searchTerm: string) => void;
  filterValues: { includeDisabledElements: boolean };
  handleOnFiltersChange: (filters: { includeDisabledElements: boolean }) => void;
};

export const BlacklistsTableToolbar: FC<BlacklistsTableToolbarProps> = ({
  onElementCreated,
  searchTerm,
  handleOnSearchTermChange,
  handleOnFiltersChange,
  filterValues,
}) => {
  const t = useT();
  const [currentSearchTerm, setCurrentSearchTerm] = useState(searchTerm);
  const createModal = useModal();

  useEffect(() => {
    setCurrentSearchTerm(searchTerm);
  }, [searchTerm]);

  const debouncedSearch = useMemo(() => {
    return debounce(handleOnSearchTermChange, debounceSearchTime);
  }, [handleOnSearchTermChange]);

  const setSearchTerm = (value: string) => {
    setCurrentSearchTerm(value);
    debouncedSearch(value);
  };

  const handleSearchInputChange = (event: "" | ChangeEvent<HTMLInputElement>) => {
    if (!event) return;
    setSearchTerm(event.target.value);
  };

  return (
    <>
      <TableToolbar aria-label="data table toolbar">
        <TableToolbarContent className="border-b border-solid border-borderSubtle01">
          <TableToolbarSearch
            placeholder={t("page.blacklists.table.search.placeholder")}
            onChange={handleSearchInputChange}
            value={currentSearchTerm}
            onClear={() => setSearchTerm("")}
            persistent={true}
          />
          <BlacklistsFilter
            onApplyFilter={handleOnFiltersChange}
            onResetFilter={() => handleOnFiltersChange({ includeDisabledElements: false })}
            values={filterValues}
          />
          <AuthorizedItem
            requiredPermissions={[
              { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.CREATE] },
            ]}
          >
            <Button renderIcon={Add} kind="secondary" onClick={createModal.openModal}>
              {t("page.blacklists.table.button.create")}
            </Button>
          </AuthorizedItem>
        </TableToolbarContent>
      </TableToolbar>
      <ModalTransition>
        {createModal.isModalOpen && (
          <BlacklistElementModal
            onClose={createModal.closeModal}
            onSuccess={() => {
              onElementCreated();
              createModal.closeModal();
            }}
          />
        )}
      </ModalTransition>
      {filterValues.includeDisabledElements && (
        <div className="flex w-full bg-layer">
          <Layer className={"p-5"}>
            <AppliedFilters
              onClear={() => handleOnFiltersChange({ includeDisabledElements: false })}
            >
              <SingleSelectAppliedFilter
                item={{
                  label: t("page.blacklists.table.filters.applied.disabled.filter.value"),
                  value: "true",
                }}
                title={t("page.blacklists.table.filters.applied.disabled.filter.title")}
                onClear={() => handleOnFiltersChange({ includeDisabledElements: false })}
              />
            </AppliedFilters>
          </Layer>
        </div>
      )}
    </>
  );
};
